// Users (SM-712): the broker accounts of the node's whole subtree (the
// default, SM-862) or only those it services directly, with status /
// balance-mode / search filters. Details opens the account on its own page,
// /users/{broker_account_id} (SM-860), in a new tab, so this list keeps its
// filters, page and scroll.
//
// An old link to the inline panel (?account=…&tab=…) redirects there.
//
//   GET  /api/broker/{id}/accounts?scope=&status=&balance_mode=&q=&offset=&limit=
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded, type Shell } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Id, Pager, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Segmented, Select } from "@/components/ui/Field";
import { fetchAccounts, keys } from "@/lib/queries";
import { accountStatusTone } from "@/lib/status";
import {
  isUserTab,
  parseScopeSearch,
  scopeFromSearch,
  scopeToSearch,
  type ScopeSearch,
  type UserTab,
} from "@/lib/trading";
import { ACCOUNT_STATUSES, BALANCE_MODES, type AccountScope } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

interface UsersSearch {
  /** Absent = the whole subtree (the default, SM-862); "direct" = the node's own. */
  scope?: ScopeSearch;
  status?: string;
  balance_mode?: string;
  q?: string;
  offset?: number;
  /** An old inline-panel link (SM-828): read only to redirect to /users/$accountId. */
  account?: string;
  /** The old link's tab, carried over by the redirect. */
  tab?: UserTab;
}

const viewGuard = capabilityGuard("VIEW");

export const Route = createFileRoute("/_app/users/")({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    scope: parseScopeSearch(s.scope),
    status: typeof s.status === "string" && s.status ? s.status : undefined,
    balance_mode: typeof s.balance_mode === "string" && s.balance_mode ? s.balance_mode : undefined,
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    offset: typeof s.offset === "number" && s.offset > 0 ? s.offset : undefined,
    account: typeof s.account === "string" && s.account ? s.account : undefined,
    tab: isUserTab(s.tab) ? s.tab : undefined,
  }),
  beforeLoad: ({ context, search }: { context: { shell: Shell }; search: UsersSearch & { node?: string; currency?: string } }) => {
    // Bookmarks and shared links to the old inline panel keep working.
    if (search.account) {
      throw redirect({
        to: "/users/$accountId",
        params: { accountId: search.account },
        search: { node: search.node, currency: search.currency, tab: search.tab } as never,
        replace: true,
      });
    }
    return viewGuard({ context });
  },
  component: UsersPage,
});

const LIMIT = 25;

function UsersPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scope = useScope();
  const accountScope: AccountScope = scopeFromSearch(search.scope);
  const shell = { node: scope.nodeId, currency: scope.currency };
  const [q, setQ] = useState(search.q ?? "");
  const params = {
    scope: accountScope,
    status: search.status,
    balance_mode: search.balance_mode,
    q: search.q,
    offset: search.offset ?? 0,
    limit: LIMIT,
  };
  const probe = { queryKey: keys.accounts(scope.nodeId, params), queryFn: () => fetchAccounts(scope.nodeId, params) };
  const listQ = useQuery({ ...probe, enabled: guard.kind === "allow" });

  const set = (patch: Partial<UsersSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        coverage={accountScope === "subtree" ? "subtree" : "node"}
      />
      <SectionCard title={t("users.filters")} className="mb-4">
        <form
          className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5"
          onSubmit={(e) => {
            e.preventDefault();
            set({ q: q.trim() || undefined });
          }}
        >
          <Segmented
            label={t("users.scope")}
            value={accountScope}
            options={[
              { value: "subtree", label: t("users.scopeSubtree") },
              { value: "direct", label: t("users.scopeDirect") },
            ]}
            onChange={(v) => set({ scope: scopeToSearch(v) })}
          />
          <Select
            label={t("common.status")}
            value={search.status ?? ""}
            onChange={(e) => set({ status: e.target.value || undefined })}
            options={[{ value: "", label: t("common.all") }, ...ACCOUNT_STATUSES.map((s) => ({ value: s, label: t(`accountStatus.${s}`) }))]}
          />
          <Select
            label={t("users.balanceMode")}
            value={search.balance_mode ?? ""}
            onChange={(e) => set({ balance_mode: e.target.value || undefined })}
            options={[{ value: "", label: t("common.all") }, ...BALANCE_MODES.map((m) => ({ value: m, label: t(`balanceMode.${m}`) }))]}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="users-q" className="text-2xs font-semibold uppercase tracking-wide text-t-3">
              {t("users.search")}
            </label>
            <input
              id="users-q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("users.searchPlaceholder")}
              className="h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
            />
          </div>
          <Button type="submit">{t("common.search")}</Button>
        </form>
      </SectionCard>

      <SectionCard title={t("users.accounts")} className="min-w-0">
        <QueryBlock query={listQ} empty={(d) => ((d.items ?? []).length === 0 ? <EmptyState title={t("users.empty")} /> : null)}>
          {(d) => (
            <>
              <TableFrame label={t("users.accounts")}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t("users.account")}</th>
                      <th scope="col">{t("users.user")}</th>
                      <th scope="col">{t("users.servicingNode")}</th>
                      <th scope="col">{t("users.balanceMode")}</th>
                      <th scope="col">{t("common.status")}</th>
                      <th scope="col">{t("users.created")}</th>
                      <th scope="col">
                        <span className="sr-only">{t("common.actions")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items ?? []).map((a) => (
                      <tr key={a.broker_account_id}>
                        <td>
                          <Link
                            to="/users/$accountId"
                            params={{ accountId: a.broker_account_id }}
                            search={shell}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mono inline-block max-w-[16ch] truncate align-bottom text-xs font-semibold text-neon hover:underline"
                            title={a.broker_account_id}
                            aria-label={t("users.openInNewTab", { id: a.broker_account_id })}
                          >
                            {a.broker_account_id}
                          </Link>
                        </td>
                        <td>
                          <Id value={a.user_id} />
                        </td>
                        <td>
                          {a.broker_id === scope.nodeId ? (
                            <span className="text-xs text-t-2">{t("users.thisNode")}</span>
                          ) : (
                            <Id value={a.broker_id} />
                          )}
                        </td>
                        <td className="text-xs">{t(`balanceMode.${a.balance_mode}`)}</td>
                        <td>
                          <Badge tone={accountStatusTone(a.status)}>{t(`accountStatus.${a.status}`)}</Badge>
                        </td>
                        <td className="text-xs">
                          <Time ms={a.created_at_unix_ms} />
                        </td>
                        <td>
                          <Link
                            to="/users/$accountId"
                            params={{ accountId: a.broker_account_id }}
                            search={shell}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-ghost btn-sm"
                            aria-label={t("users.openInNewTab", { id: a.broker_account_id })}
                          >
                            {t("users.details")}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableFrame>
              <Pager page={d.page} onOffset={(o) => set({ offset: o || undefined })} />
            </>
          )}
        </QueryBlock>
      </SectionCard>
    </Guarded>
  );
}

